import NicknameGenerator from '../utils/NicknameGenerator.js';
import ProfanityFilter from '../utils/ProfanityFilter.js';

/**
 * MultiplayerPanel — Room browser, create/join, share URL, connected players.
 * Renders as a section inside the Settings > Game tab.
 */
export default class MultiplayerPanel {
  /**
   * @param {HTMLElement} container - The settings tab container to append to
   * @param {Object} callbacks
   * @param {function(string, Object): Promise} callbacks.onJoin - Join room (roomId, options)
   * @param {function(): void} callbacks.onLeave - Leave room
   * @param {function(): Promise<Array>} callbacks.listRooms - Fetch public rooms
   * @param {function(string): void} [callbacks.onNicknameChange] - Called when nickname changes
   */
  constructor(container, callbacks) {
    this.callbacks = callbacks;
    this._isConnected = false;
    this._roomId = null;
    this._peerCount = 0;
    this._nickname = NicknameGenerator.getOrCreate();
    this._displayNickname = null; // Dedup'd name (e.g., "Player#2") — set by main.js

    this._createDOM(container);
    this._setupEvents();
  }

  _createDOM(container) {
    this.section = document.createElement('div');
    this.section.className = 'gui-section';

    this.section.innerHTML = `
      <div class="gui-section-header">
        <h3 class="gui-section-title">Multiplayer</h3>
        <svg class="gui-section-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </div>
      <div class="gui-section-body">
        <!-- Join / Create -->
        <div id="mp-join-area">
          <div class="gui-row gui-row-stacked">
            <label class="gui-label gui-label-stacked">Nickname</label>
            <div style="display:flex; gap:4px;">
              <input type="text" id="mp-nickname-input" class="gui-input" placeholder="Your name..." maxlength="16" style="flex:1;">
              <button id="mp-nickname-rand" class="gui-button" title="Randomize" style="padding:4px 8px; font-size:14px; min-width:32px;">&#x1F3B2;</button>
            </div>
          </div>
          <div class="gui-row gui-row-stacked">
            <label class="gui-label gui-label-stacked">Room Name</label>
            <input type="text" id="mp-room-input" class="gui-input" placeholder="Enter room name..." maxlength="40" autocomplete="off">
          </div>
          <div class="gui-row" style="margin-top: 4px;">
            <label style="display: flex; align-items: center; gap: 6px; cursor: pointer; font-size: 12px; color: #b0b0b0; user-select: none;">
              <input type="checkbox" id="mp-password-check" style="accent-color: #6a9eda;">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              Password protect
            </label>
          </div>
          <div id="mp-password-row" class="gui-row gui-row-stacked" style="display: none;">
            <label class="gui-label gui-label-stacked">Password</label>
            <input type="text" id="mp-password-input" class="gui-input" placeholder="Room password..." maxlength="32" autocomplete="off">
          </div>
          <div id="mp-join-error" style="display:none; color:#e06060; font-size:12px; padding:4px 2px;"></div>
          <div class="gui-button-group" style="margin-top: 6px;">
            <button id="mp-join-btn" class="gui-button gui-button-primary">Join / Create</button>
            <button id="mp-browse-btn" class="gui-button gui-button-success">Browse Rooms</button>
          </div>

          <!-- Room browser (hidden by default) -->
          <div id="mp-room-list" style="display: none; margin-top: 8px;">
            <div id="mp-room-list-content" style="max-height: 150px; overflow-y: auto; border: 1px solid rgba(255,255,255,0.1); border-radius: 4px; background: rgba(0,0,0,0.2);"></div>
          </div>
        </div>

        <!-- Connected state (hidden by default) -->
        <div id="mp-connected-area" style="display: none;">
          <div id="mp-nick-display" class="gui-info" style="margin-bottom: 4px; font-size: 12px; color: #a0a0a0;"></div>
          <div id="mp-status" class="gui-info" style="margin-bottom: 8px;"></div>
          <div class="gui-button-group">
            <button id="mp-share-btn" class="gui-button gui-button-success">Copy Link</button>
            <button id="mp-leave-btn" class="gui-button gui-button-danger">Leave</button>
          </div>
        </div>
      </div>
    `;

    container.appendChild(this.section);
  }

  _setupEvents() {
    // Collapse toggle
    this.section.querySelector('.gui-section-header').addEventListener('click', () => {
      this.section.classList.toggle('collapsed');
    });

    // Nickname
    const nickInput = document.getElementById('mp-nickname-input');
    nickInput.value = this._nickname;
    // Only 'change' (not 'blur') — prevents saving auto-generated names when user
    // clicks Join without editing. 'change' only fires after actual user interaction.
    nickInput.addEventListener('change', () => this._handleNicknameChange(nickInput.value));

    document.getElementById('mp-nickname-rand').addEventListener('click', () => {
      const nick = NicknameGenerator.generate();
      nickInput.value = nick;
      this._handleNicknameChange(nick);
    });

    // Password toggle
    document.getElementById('mp-password-check').addEventListener('change', (e) => {
      document.getElementById('mp-password-row').style.display = e.target.checked ? 'block' : 'none';
      if (e.target.checked) document.getElementById('mp-password-input').focus();
    });

    // Join / Create
    document.getElementById('mp-join-btn').addEventListener('click', () => this._handleJoin());
    document.getElementById('mp-room-input').addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this._handleJoin();
    });
    document.getElementById('mp-password-input').addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this._handleJoin();
    });

    // Browse
    document.getElementById('mp-browse-btn').addEventListener('click', () => this._handleBrowse());

    // Leave
    document.getElementById('mp-leave-btn').addEventListener('click', () => this._handleLeave());

    // Share
    document.getElementById('mp-share-btn').addEventListener('click', () => this._handleShare());
  }

  _handleNicknameChange(value) {
    const nick = value.trim().slice(0, 16) || NicknameGenerator.generate();
    this._nickname = nick;
    NicknameGenerator.save(nick);
    const input = document.getElementById('mp-nickname-input');
    if (input.value !== nick) input.value = nick;
    this.callbacks.onNicknameChange?.(nick);
  }

  /** Get the current nickname. */
  getNickname() {
    return this._nickname;
  }

  /** Update the displayed nickname (e.g., after dedup adds #2 suffix). */
  setDisplayNickname(nick) {
    this._displayNickname = nick;
    if (this._isConnected) {
      const el = document.getElementById('mp-nick-display');
      if (el) el.textContent = `Playing as: ${nick}`;
    }
  }

  async _handleJoin() {
    const input = document.getElementById('mp-room-input');
    const roomId = input.value.trim();
    if (!roomId) {
      input.focus();
      return;
    }

    const joinError = document.getElementById('mp-join-error');

    if (ProfanityFilter.containsProfanity(roomId)) {
      const censored = ProfanityFilter.filterAlways(roomId);
      joinError.textContent = `Can't create room "${censored}" — inappropriate language.`;
      joinError.style.display = 'block';
      input.focus();
      return;
    }

    const hasPassword = document.getElementById('mp-password-check').checked;
    const password = hasPassword ? document.getElementById('mp-password-input').value.trim() : null;

    if (hasPassword && !password) {
      joinError.textContent = 'Please enter a password.';
      joinError.style.display = 'block';
      document.getElementById('mp-password-input').focus();
      return;
    }
    joinError.style.display = 'none';

    const joinBtn = document.getElementById('mp-join-btn');
    joinBtn.disabled = true;
    joinBtn.textContent = 'Connecting...';

    try {
      await this.callbacks.onJoin(roomId, {
        name: roomId,
        isPublic: true,
        password,
        hasPassword: !!password
      });
      this._showConnected(roomId, !!password);
    } catch (err) {
      if (err.message !== 'Cancelled') {
        this.setStatus('Failed to join: ' + err.message);
      }
    } finally {
      joinBtn.disabled = false;
      joinBtn.textContent = 'Join / Create';
    }
  }

  async _handleBrowse() {
    const listDiv = document.getElementById('mp-room-list');
    const contentDiv = document.getElementById('mp-room-list-content');
    const isVisible = listDiv.style.display !== 'none';

    if (isVisible) {
      listDiv.style.display = 'none';
      return;
    }

    contentDiv.innerHTML = '<div style="padding: 8px; color: #aaa; text-align: center;">Loading...</div>';
    listDiv.style.display = 'block';

    try {
      const rooms = await this.callbacks.listRooms();

      if (rooms.length === 0) {
        contentDiv.innerHTML = '<div style="padding: 8px; color: #aaa; text-align: center;">No rooms available</div>';
        return;
      }

      contentDiv.innerHTML = rooms.map(room => `
        <div class="mp-room-entry" data-room-id="${this._escapeHtml(room.id)}" data-has-password="${room.hasPassword ? 'true' : 'false'}" style="
          padding: 8px 10px; cursor: pointer; border-bottom: 1px solid rgba(255,255,255,0.05);
          display: flex; justify-content: space-between; align-items: center;
          transition: background 0.15s;
        " onmouseenter="this.style.background='rgba(255,255,255,0.08)'"
           onmouseleave="this.style.background='transparent'">
          <span style="color: #e0e0e0; font-size: 13px; display: flex; align-items: center; gap: 5px;">
            ${room.hasPassword ? '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#aaa" stroke-width="2.5"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>' : ''}
            ${this._escapeHtml(ProfanityFilter.filter(room.name))}
          </span>
          <span style="color: #aaa; font-size: 12px;">${room.playerCount}/${room.maxPlayers}</span>
        </div>
      `).join('');

      // Click to join (password rooms show password field first)
      contentDiv.querySelectorAll('.mp-room-entry').forEach(entry => {
        entry.addEventListener('click', () => {
          const roomId = entry.dataset.roomId;
          const hasPassword = entry.dataset.hasPassword === 'true';

          document.getElementById('mp-room-input').value = roomId;
          listDiv.style.display = 'none';

          if (hasPassword) {
            document.getElementById('mp-password-check').checked = true;
            document.getElementById('mp-password-row').style.display = 'block';
            document.getElementById('mp-password-input').value = '';
            document.getElementById('mp-password-input').focus();
          } else {
            document.getElementById('mp-password-check').checked = false;
            document.getElementById('mp-password-row').style.display = 'none';
            this._handleJoin();
          }
        });
      });
    } catch (err) {
      contentDiv.innerHTML = `<div style="padding: 8px; color: #ff6b6b; text-align: center;">Error: ${err.message}</div>`;
    }
  }

  _handleLeave() {
    this.callbacks.onLeave();
    this._showDisconnected();
  }

  _handleShare() {
    if (!this._roomId) return;
    const url = `${window.location.origin}${window.location.pathname}?room=${encodeURIComponent(this._roomId)}`;
    navigator.clipboard.writeText(url).then(() => {
      const btn = document.getElementById('mp-share-btn');
      const original = btn.textContent;
      btn.textContent = 'Copied!';
      setTimeout(() => { btn.textContent = original; }, 1500);
    }).catch(() => {
      // Fallback: select and copy
      prompt('Share this link:', url);
    });
  }

  _showConnected(roomId, hasPassword = false) {
    this._isConnected = true;
    this._roomId = roomId;
    this._hasPassword = hasPassword;
    document.getElementById('mp-join-area').style.display = 'none';
    document.getElementById('mp-connected-area').style.display = 'block';
    const nickDisplay = document.getElementById('mp-nick-display');
    if (nickDisplay) nickDisplay.textContent = `Playing as: ${this._displayNickname || this._nickname}`;
    this.setStatus(`Connected to "${ProfanityFilter.filter(roomId)}"${hasPassword ? ' (locked)' : ''}`);
  }

  _showDisconnected() {
    this._isConnected = false;
    this._roomId = null;
    this._hasPassword = false;
    this._peerCount = 0;
    document.getElementById('mp-join-area').style.display = 'block';
    document.getElementById('mp-connected-area').style.display = 'none';
    document.getElementById('mp-room-list').style.display = 'none';
    // Reset password UI
    document.getElementById('mp-password-check').checked = false;
    document.getElementById('mp-password-row').style.display = 'none';
    document.getElementById('mp-password-input').value = '';
  }

  /**
   * Update the status text in connected state.
   * @param {string} text
   */
  setStatus(text) {
    const el = document.getElementById('mp-status');
    if (el) el.textContent = text;
  }

  /**
   * Update the displayed peer count.
   * @param {number} count
   */
  setPeerCount(count) {
    this._peerCount = count;
    if (this._isConnected) {
      const lock = this._hasPassword ? ' (locked)' : '';
      this.setStatus(`Connected to "${ProfanityFilter.filter(this._roomId)}"${lock} - ${count} peer${count !== 1 ? 's' : ''}`);
    }
  }

  /**
   * Programmatically leave the room (e.g., when loading a new world).
   */
  leave() {
    if (this._isConnected) {
      this._handleLeave();
    }
  }

  /**
   * Auto-join a room (e.g., from URL param).
   * If the room is password-protected, shows the password input instead.
   * @param {string} roomId
   */
  async autoJoin(roomId) {
    document.getElementById('mp-room-input').value = roomId;

    // Check if room requires a password before auto-joining
    try {
      const roomInfo = await this.callbacks.roomInfo?.(roomId);
      if (roomInfo?.hasPassword) {
        // Show password UI instead of auto-joining
        document.getElementById('mp-password-check').checked = true;
        document.getElementById('mp-password-row').style.display = 'block';
        document.getElementById('mp-password-input').value = '';
        document.getElementById('mp-password-input').focus();
        this.section.classList.remove('collapsed');
        return;
      }
    } catch {
      // If check fails, proceed with normal join
    }

    this._handleJoin();
  }

  _escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}
